import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFlask,
  faGlobe,
  faDesktop,
  faPlus,
  faLink,
  faCamera,
  faArrowRight,
  faSpinner,
  faChevronDown,
  faChevronUp,
  faPlay,
  faFileAlt,
  faShieldAlt,
  faTimes,
  faExternalLinkAlt,
  faRobot,
  faCheckCircle,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import actionService from '../../services/actionService';
import mitreService from '../../services/mitreService';
import targetService from '../../services/targetService';
import projectService from '../../services/projectService';
import atomicService from '../../services/atomicService';
import agentService from '../../services/agentService';
import MitreTechniqueSelector from '../mitre/MitreTechniqueSelector';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { compressImage } from '../../utils/imageUtils';

const ActionForm = ({ action = null, isEditing = false }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { darkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mitreTechniques, setMitreTechniques] = useState([]);
  const [selectedAtomicTest, setSelectedAtomicTest] = useState(null);
  const [testParameters, setTestParameters] = useState({});
  const [availableTargets, setAvailableTargets] = useState([]);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [availableAtomicTests, setAvailableAtomicTests] = useState([]);
  const [showAtomicTestsPanel, setShowAtomicTestsPanel] = useState(false);
  const [loadingAtomicTests, setLoadingAtomicTests] = useState(false);
  const [showTechniqueSelector, setShowTechniqueSelector] = useState(false);
  const [selectedTechniqueDescription, setSelectedTechniqueDescription] = useState('');

  // Execute modal state
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [executeTestData, setExecuteTestData] = useState(null);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [executeInputValues, setExecuteInputValues] = useState({});
  const [executing, setExecuting] = useState(false);
  const [executeResult, setExecuteResult] = useState(null);
  const [loadingAgents, setLoadingAgents] = useState(false);

  // Add target modal state
  const [showAddTargetModal, setShowAddTargetModal] = useState(false);
  const [newTarget, setNewTarget] = useState({ type: 'hostname', value: '', description: '' });
  const [addingTarget, setAddingTarget] = useState(false);
  
  
  // Helper function to get local datetime string for input
  const getLocalDateTime = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    project: projectId,
    title: '',
    description: '',
    startTimestamp: getLocalDateTime(),
    endTimestamp: '',
    mitreAttackTechnique: null,
    result: 'unknown',
    notes: '',
    evidence: [],
    operator: '',
    targets: [],
    sources: []
  });

  // Initialize form data when editing
  useEffect(() => {
    if (isEditing && action) {
      setFormData({
        project: action.project._id || action.project || projectId,
        title: action.title || '',
        description: action.description || '',
        startTimestamp: action.startTimestamp ? getLocalDateTime(new Date(action.startTimestamp)) : getLocalDateTime(),
        endTimestamp: action.endTimestamp ? getLocalDateTime(new Date(action.endTimestamp)) : '',
        mitreAttackTechnique: action.mitreAttackTechnique || null,
        result: action.result || 'unknown',
        notes: action.notes || '',
        evidence: action.evidence || [],
        operator: action.operator || '',
        targets: action.targets || [],
        sources: action.sources || []
      });

      // Set selected targets
      if (action.targets && action.targets.length > 0) {
        const targetIds = action.targets.map(target =>
          typeof target === 'object' ? target._id : target
        );
        setSelectedTargets(targetIds);
      }

      // Set selected sources
      if (action.sources && action.sources.length > 0) {
        const sourceIds = action.sources.map(source =>
          typeof source === 'object' ? source._id : source
        );
        setSelectedSources(sourceIds);
      }

      // Process evidence to separate files
      if (action.evidence && action.evidence.length > 0) {
        const files = [];
        action.evidence.forEach((item, index) => {
          if (item.startsWith('data:image')) {
            files.push({
              name: `Evidence ${index + 1}`,
              type: 'image',
              data: item,
              size: 0
            });
          }
        });
        setEvidenceFiles(files);
      }
    }
  }, [isEditing, action, projectId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch project details for team members
        const project = await projectService.getProject(projectId);
        setTeamMembers(project.team || []);
        
        // Fetch MITRE techniques
        const techniques = await mitreService.getTechniques();
        setMitreTechniques(techniques);
        
        // Fetch targets for the project
        const targets = await targetService.getTargetsByProject(projectId);
        setAvailableTargets(targets);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [projectId]);

  // Auto-select operator: prefer current user, fallback to first team member
  useEffect(() => {
    if (!isEditing && !formData.operator) {
      // First try to use the current logged-in user
      if (user?.displayName) {
        setFormData(prev => ({
          ...prev,
          operator: user.displayName
        }));
      } else if (user?.username) {
        setFormData(prev => ({
          ...prev,
          operator: user.username
        }));
      } else if (teamMembers.length === 1) {
        // Fallback to first team member if only one
        setFormData(prev => ({
          ...prev,
          operator: teamMembers[0].name
        }));
      }
    }
  }, [teamMembers, isEditing, formData.operator, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMitreChange = async (e) => {
    const techniqueId = e.target.value;
    if (techniqueId) {
      const technique = mitreTechniques.find(t => t.id === techniqueId);
      setFormData(prev => ({
        ...prev,
        mitreAttackTechnique: {
          id: technique.id,
          name: technique.name,
          tactic: technique.tactic
        }
      }));
      setSelectedTechniqueDescription(technique.description || '');

      // Fetch available atomic tests for this technique
      setLoadingAtomicTests(true);
      try {
        const tests = await atomicService.getAtomicTestsByTechnique(techniqueId);
        setAvailableAtomicTests(tests);
        if (tests.length > 0) {
          setShowAtomicTestsPanel(true);
        }
      } catch (error) {
        console.error('Error fetching atomic tests:', error);
        setAvailableAtomicTests([]);
      } finally {
        setLoadingAtomicTests(false);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        mitreAttackTechnique: null
      }));
      setSelectedTechniqueDescription('');
      setAvailableAtomicTests([]);
      setShowAtomicTestsPanel(false);
    }
  };

  // Handle technique selection from unified selector (with optional atomic test)
  const handleTechniqueSelect = async (selection) => {
    const { atomicTest } = selection;

    // Check if an atomic test was selected
    if (atomicTest) {
      // User selected a specific atomic test - auto-fill everything
      const { individualTest, technique } = atomicTest;

      setFormData(prev => ({
        ...prev,
        title: individualTest.name,
        description: `${individualTest.description}\n\nAtomic Test: ${atomicTest.atomicTest.name}\nTechnique: ${technique.id} - ${technique.name}`,
        mitreAttackTechnique: {
          id: technique.id,
          name: technique.name,
          tactic: selection.tactic,
          url: technique.url
        }
      }));

      // Store selected test data
      setSelectedAtomicTest(atomicTest);

      // Initialize parameters with defaults
      if (individualTest.inputArguments) {
        const params = {};
        individualTest.inputArguments.forEach(arg => {
          params[arg.name] = arg.default || '';
        });
        setTestParameters(params);
      }

      setSelectedTechniqueDescription(technique.description || '');
      setShowTechniqueSelector(false);

      // Fetch and show available atomic tests for this technique
      setLoadingAtomicTests(true);
      try {
        const tests = await atomicService.getAtomicTestsByTechnique(technique.id);
        setAvailableAtomicTests(Array.isArray(tests) ? tests : []);
        setShowAtomicTestsPanel(false); // Keep collapsed since user already picked one
      } catch (error) {
        console.error('Error fetching atomic tests:', error);
        setAvailableAtomicTests([]);
      } finally {
        setLoadingAtomicTests(false);
      }
    } else {
      // User selected just the technique (no atomic test)
      const techniqueTitle = `${selection.id} - ${selection.name}`;
      const techniqueDesc = selection.description || '';

      setFormData(prev => ({
        ...prev,
        // Only pre-fill title if it's empty or was auto-filled from previous technique
        title: !prev.title || prev.title.startsWith('T') ? techniqueTitle : prev.title,
        // Only pre-fill description if it's empty
        description: !prev.description ? techniqueDesc : prev.description,
        mitreAttackTechnique: {
          id: selection.id,
          name: selection.name,
          tactic: selection.tactic,
          url: selection.url
        }
      }));
      setSelectedTechniqueDescription(techniqueDesc);
      setShowTechniqueSelector(false);

      // Fetch available atomic tests for this technique
      setLoadingAtomicTests(true);
      try {
        const tests = await atomicService.getAtomicTestsByTechnique(selection.id);
        setAvailableAtomicTests(tests);
        if (tests.length > 0) {
          setShowAtomicTestsPanel(true);
        }
      } catch (error) {
        console.error('Error fetching atomic tests:', error);
        setAvailableAtomicTests([]);
      } finally {
        setLoadingAtomicTests(false);
      }
    }
  };

  const handleTechniqueSelectorClose = () => {
    setShowTechniqueSelector(false);
  };

  // Add new target handler
  const handleAddTarget = async () => {
    if (!newTarget.value.trim()) return;

    setAddingTarget(true);
    try {
      const createdTarget = await targetService.createTarget({
        project: projectId,
        type: newTarget.type,
        value: newTarget.value.trim(),
        description: newTarget.description.trim()
      });

      // Add to available targets list
      setAvailableTargets(prev => [...prev, createdTarget]);

      // Reset form and close modal
      setNewTarget({ type: 'hostname', value: '', description: '' });
      setShowAddTargetModal(false);
    } catch (error) {
      console.error('Error creating target:', error);
      alert(error.response?.data?.message || 'Failed to create target');
    } finally {
      setAddingTarget(false);
    }
  };

  // Open execute modal for an atomic test
  const handleExecuteTest = async (test, individualTest) => {
    setExecuteTestData({ test, individualTest });
    setExecuteResult(null);

    // Initialize input values with defaults
    const initialValues = {};
    if (individualTest.inputArguments) {
      individualTest.inputArguments.forEach(arg => {
        initialValues[arg.name] = arg.default || '';
      });
    }
    setExecuteInputValues(initialValues);

    // Fetch available agents
    setLoadingAgents(true);
    try {
      const agents = await agentService.getAgents();
      // Filter to only online/connected agents
      const onlineAgents = agents.filter(a => a.status === 'online' || a.isConnected);
      setAvailableAgents(onlineAgents);
      if (onlineAgents.length > 0) {
        setSelectedAgentId(onlineAgents[0]._id);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      setAvailableAgents([]);
    } finally {
      setLoadingAgents(false);
    }

    setShowExecuteModal(true);
  };

  // Execute the test on selected agent
  const handleExecuteConfirm = async () => {
    if (!selectedAgentId || !executeTestData) return;

    setExecuting(true);
    setExecuteResult(null);

    try {
      const result = await atomicService.executeTest(
        executeTestData.test._id,
        selectedAgentId,
        {
          inputValues: executeInputValues,
          project: projectId,
          createAction: true
        }
      );

      setExecuteResult({
        success: true,
        message: 'Test execution started successfully!',
        taskId: result.taskId,
        actionId: result.actionId
      });

      // Also pre-fill the form with this test data
      handleUseAsTemplate(executeTestData.test, executeTestData.individualTest);

    } catch (error) {
      console.error('Error executing test:', error);
      setExecuteResult({
        success: false,
        message: error.response?.data?.message || 'Failed to execute test'
      });
    } finally {
      setExecuting(false);
    }
  };

  // Use an atomic test from the available tests panel as a template
  const handleUseAsTemplate = (test, individualTest) => {
    // Find the technique info
    const technique = mitreTechniques.find(t => t.id === test.techniqueId);

    setFormData(prev => ({
      ...prev,
      title: individualTest.name,
      description: individualTest.description || test.description,
      mitreAttackTechnique: {
        id: test.techniqueId,
        name: test.displayName || test.name,
        tactic: test.tactic || technique?.tactic || 'unknown'
      }
    }));

    // Store selected test data for parameter handling
    setSelectedAtomicTest({
      atomicTest: test,
      individualTest,
      technique: {
        id: test.techniqueId,
        name: test.displayName || test.name,
        tactic: test.tactic || technique?.tactic || 'unknown'
      },
      inputArguments: individualTest.inputArguments || []
    });

    // Initialize parameters with defaults
    if (individualTest.inputArguments) {
      const params = {};
      individualTest.inputArguments.forEach(arg => {
        params[arg.name] = arg.default || '';
      });
      setTestParameters(params);
    }

    // Collapse the panel
    setShowAtomicTestsPanel(false);
  };

  const handleAtomicTestSelect = async (testData) => {
    const { atomicTest, individualTest, technique } = testData;

    // Set form data from atomic test
    setFormData(prev => ({
      ...prev,
      title: individualTest.name,
      description: `${individualTest.description}\n\nAtomic Test: ${atomicTest.name}`,
      mitreAttackTechnique: {
        id: technique.id,
        name: technique.name,
        tactic: technique.tactic
      }
    }));

    // Store selected test data
    setSelectedAtomicTest(testData);

    // Initialize parameters with defaults
    if (individualTest.inputArguments) {
      const params = {};
      individualTest.inputArguments.forEach(arg => {
        params[arg.name] = arg.default || '';
      });
      setTestParameters(params);
    }

    // Also fetch available atomic tests for this technique
    if (technique.id) {
      setLoadingAtomicTests(true);
      try {
        const tests = await atomicService.getAtomicTestsByTechnique(technique.id);
        setAvailableAtomicTests(Array.isArray(tests) ? tests : []);
        setShowAtomicTestsPanel(false); // Keep collapsed since user already picked one
      } catch (error) {
        console.error('Error fetching atomic tests:', error);
        setAvailableAtomicTests([]);
      } finally {
        setLoadingAtomicTests(false);
      }
    }
  };

  const handleParameterChange = (paramName, value) => {
    setTestParameters(prev => ({
      ...prev,
      [paramName]: value
    }));
  };

  const getExecutableCommand = () => {
    if (!selectedAtomicTest) return '';
    
    let command = selectedAtomicTest.individualTest.executor.command;
    
    // Replace parameters in command
    Object.entries(testParameters).forEach(([key, value]) => {
      const placeholder = `#{${key}}`;
      command = command.replace(new RegExp(placeholder, 'g'), value);
    });
    
    return command;
  };

  const handleEvidenceAdd = () => {
    const url = prompt('Enter evidence URL:');
    if (url) {
      setFormData(prev => ({
        ...prev,
        evidence: [...prev.evidence, url]
      }));
    }
  };
    
  
// Update the handleEvidenceFileUpload function
const handleEvidenceFileUpload = async (e) => {
  const files = Array.from(e.target.files);
  
  for (const file of files) {
    // Check if it's an image
    if (file.type.startsWith('image/')) {
      try {
        // Compress the image
        const compressedBase64 = await compressImage(file, 1200, 1200, 0.7);
        
        // Add to evidence files for preview
        setEvidenceFiles(prev => [...prev, {
          name: file.name,
          type: 'image',
          data: compressedBase64,
          size: compressedBase64.length // Approximate size
        }]);
        
        // Add base64 to evidence array
        setFormData(prev => ({
          ...prev,
          evidence: [...prev.evidence, compressedBase64]
        }));
      } catch (error) {
        console.error('Error compressing image:', error);
        alert('Failed to process image. Please try a smaller image.');
      }
    }
  }
  
  // Clear the input
  e.target.value = null;
};


  const handleEvidenceRemove = (index) => {
    // Remove from evidence array
    setFormData(prev => ({
      ...prev,
      evidence: prev.evidence.filter((_, i) => i !== index)
    }));
    
    // Remove from evidence files if it's an image
    const evidenceItem = formData.evidence[index];
    if (evidenceItem && evidenceItem.startsWith('data:image')) {
      setEvidenceFiles(prev => prev.filter(file => file.data !== evidenceItem));
    }
  };

  const handleTargetToggle = (targetId) => {
    setSelectedTargets(prev => {
      if (prev.includes(targetId)) {
        return prev.filter(id => id !== targetId);
      } else {
        return [...prev, targetId];
      }
    });
  };

  const handleSourceToggle = (sourceId) => {
    setSelectedSources(prev => {
      if (prev.includes(sourceId)) {
        return prev.filter(id => id !== sourceId);
      } else {
        return [...prev, sourceId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Add atomic test information to notes if selected
      let finalNotes = formData.notes;
      if (selectedAtomicTest) {
        const atomicInfo = `\n\n--- Atomic Test Information ---\n` +
          `Test: ${selectedAtomicTest.individualTest.name}\n` +
          `Executor: ${selectedAtomicTest.individualTest.executor.type}\n` +
          `Command: ${getExecutableCommand()}\n`;
        
        if (selectedAtomicTest.individualTest.executor.cleanupCommand) {
          finalNotes += `Cleanup: ${selectedAtomicTest.individualTest.executor.cleanupCommand}\n`;
        }
        
        finalNotes = formData.notes + atomicInfo;
      }

      const actionData = {
        ...formData,
        notes: finalNotes,
        targets: selectedTargets,
        sources: selectedSources
      };

      if (isEditing && action) {
        // Update existing action
        await actionService.updateAction(action._id, actionData);
        alert('Action updated successfully!');
      } else {
        // Create new action
        await actionService.createAction(actionData);
        alert('Action logged successfully!');
      }
      
      navigate(`/projects/${projectId}?tab=actions`);
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} action:`, error);
      setError(error.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} action`);
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">
        {isEditing ? t('actions.editAction') : t('actions.logNewAction')}
      </h1>


      {/* Selected Atomic Test Details */}
      {selectedAtomicTest && (
        <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
          <h3 className="font-semibold text-orange-800 mb-3">
            {t('actions.selectedAtomicConfig') || 'Selected Atomic Test Configuration'}
          </h3>
          
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-700">
                <span className="font-medium">{t('common.test')}:</span> {selectedAtomicTest.individualTest.name}
              </p>
              <p className="text-sm text-gray-700">
                <span className="font-medium">{t('common.technique')}:</span> {selectedAtomicTest.technique.id} - {selectedAtomicTest.technique.name}
              </p>
              <p className="text-sm text-gray-700">
                <span className="font-medium">{t('common.executor')}:</span> {selectedAtomicTest.individualTest.executor.type}
              </p>
            </div>

            {/* Test Parameters */}
            {selectedAtomicTest.inputArguments && selectedAtomicTest.inputArguments.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-800 mb-2">{t('actions.testParameters')}</h4>
                <div className="space-y-3">
                  {selectedAtomicTest.inputArguments.map(arg => (
                    <div key={arg.name}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {arg.name}
                        {arg.description && (
                          <span className="ml-2 text-gray-500 font-normal">
                            - {arg.description}
                          </span>
                        )}
                      </label>
                      <input
                        type="text"
                        value={testParameters[arg.name] || ''}
                        onChange={(e) => handleParameterChange(arg.name, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        placeholder={`Default: ${arg.default}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Command Preview */}
            <div>
              <h4 className="font-medium text-gray-800 mb-2">{t('actions.commandToExecute')}</h4>
              <pre className="bg-gray-800 text-gray-100 p-3 rounded-md text-sm overflow-x-auto">
                {getExecutableCommand()}
              </pre>
            </div>

            {/* Cleanup Command */}
            {selectedAtomicTest.individualTest.executor.cleanupCommand && (
              <div>
                <h4 className="font-medium text-gray-800 mb-2">{t('actions.cleanupCommand')}</h4>
                <pre className="bg-gray-800 text-gray-100 p-3 rounded-md text-sm overflow-x-auto">
                  {selectedAtomicTest.individualTest.executor.cleanupCommand}
                </pre>
              </div>
            )}

            {/* Execute on Agent Button */}
            <div className="flex gap-2 pt-2 border-t border-orange-200">
              <button
                type="button"
                onClick={() => handleExecuteTest(selectedAtomicTest.atomicTest, selectedAtomicTest.individualTest)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
              >
                <FontAwesomeIcon icon={faPlay} className="mr-2" />
                {t('actions.executeOnAgent')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelectedAtomicTest(null);
                  setTestParameters({});
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                {t('common.clearSelection')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Form */}
      <div className={`rounded-lg shadow-md p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {error && (
          <div className={`mb-4 border px-4 py-3 rounded ${darkMode ? 'bg-red-900/50 border-red-500 text-red-200' : 'bg-red-50 border-red-200 text-red-700'}`}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div className="mb-4">
            <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} htmlFor="title">
              {t('actions.actionTitle')} *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              required
              disabled={loading}
            />
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} htmlFor="description">
              {t('common.description')} *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              required
              disabled={loading}
            />
          </div>

          {/* Timestamps */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} htmlFor="startTimestamp">
                {t('actions.startTime')} *
              </label>
              <input
                type="datetime-local"
                id="startTimestamp"
                name="startTimestamp"
                value={formData.startTimestamp}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                required
                disabled={loading}
              />
            </div>
            <div>
              <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} htmlFor="endTimestamp">
                {t('actions.endTime')}
              </label>
              <input
                type="datetime-local"
                id="endTimestamp"
                name="endTimestamp"
                value={formData.endTimestamp}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                disabled={loading}
              />
            </div>
          </div>

          {/* MITRE Technique and Result */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} htmlFor="mitreAttackTechnique">
                MITRE ATT&CK Technique
              </label>
              <button
                type="button"
                onClick={() => setShowTechniqueSelector(true)}
                className={`w-full px-3 py-2 border rounded-md text-left flex items-center justify-between ${
                  formData.mitreAttackTechnique
                    ? darkMode ? 'border-blue-500 bg-blue-900/30 text-white' : 'border-blue-500 bg-blue-50 text-gray-900'
                    : darkMode ? 'border-gray-700 hover:border-gray-600 bg-gray-900 text-gray-400' : 'border-gray-300 hover:border-gray-400 bg-white text-gray-500'
                }`}
                disabled={loading}
              >
                {formData.mitreAttackTechnique ? (
                  <div className="flex items-center flex-1 min-w-0">
                    <span className="font-mono text-sm px-2 py-0.5 bg-red-100 text-red-700 rounded mr-2">
                      {formData.mitreAttackTechnique.id}
                    </span>
                    <span className="truncate text-gray-700">{formData.mitreAttackTechnique.name}</span>
                  </div>
                ) : (
                  <span className="text-gray-500">{t('actions.clickToSelectTechnique')}</span>
                )}
                <FontAwesomeIcon icon={faShieldAlt} className="text-gray-400 ml-2" />
              </button>
              {formData.mitreAttackTechnique && (
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {formData.mitreAttackTechnique.tactic}
                  </span>
                  <div className="flex items-center gap-2">
                    {formData.mitreAttackTechnique.url && (
                      <a
                        href={formData.mitreAttackTechnique.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-500 hover:text-blue-700"
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                        MITRE
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, mitreAttackTechnique: null }));
                        setSelectedTechniqueDescription('');
                        setAvailableAtomicTests([]);
                        setShowAtomicTestsPanel(false);
                      }}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      <FontAwesomeIcon icon={faTimes} className="mr-1" />
                      {t('common.clear')}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} htmlFor="result">
                {t('actions.result')} *
              </label>
              <select
                id="result"
                name="result"
                value={formData.result}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                required
                disabled={loading}
              >
                <option value="unknown">{t('actions.unknown')}</option>
                <option value="successful">{t('actions.successful')}</option>
                <option value="detected">{t('actions.detected')}</option>
                <option value="blocked">{t('actions.blocked')}</option>
                <option value="failed">{t('actions.failed')}</option>
                <option value="partial">{t('actions.partial')}</option>
              </select>
            </div>
          </div>

          {/* Technique Description - shown when technique is selected */}
          {formData.mitreAttackTechnique && selectedTechniqueDescription && (
            <div className={`mb-4 border rounded-lg p-4 ${darkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
              <h4 className={`text-sm font-bold mb-2 flex items-center ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                <FontAwesomeIcon icon={faShieldAlt} className="mr-2" />
                {t('actions.techniqueDescription')}
              </h4>
              <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {selectedTechniqueDescription}
              </p>
            </div>
          )}

          {/* Available Atomic Tests Panel - shown when technique is selected */}
          {formData.mitreAttackTechnique && (
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setShowAtomicTestsPanel(!showAtomicTestsPanel)}
                className={`w-full flex items-center justify-between p-3 border rounded-lg text-left transition-colors ${darkMode ? 'bg-orange-900/30 border-orange-700 hover:bg-orange-900/40' : 'bg-orange-50 border-orange-200 hover:bg-orange-100'}`}
              >
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faFlask} className="text-orange-500 mr-2" />
                  <span className="font-medium text-orange-800">
                    {(t('actions.availableAtomicTestsFor') || 'Available Atomic Tests for {{id}}').replace('{{id}}', formData.mitreAttackTechnique.id)}
                  </span>
                  {loadingAtomicTests && (
                    <FontAwesomeIcon icon={faSpinner} className="ml-2 animate-spin text-orange-500" />
                  )}
                  {!loadingAtomicTests && availableAtomicTests.length > 0 && (
                    <span className="ml-2 px-2 py-1 bg-orange-200 text-orange-800 text-xs rounded-full">
                      {(t('actions.testCount') || '{{count}} tests').replace('{{count}}', availableAtomicTests.reduce((sum, at) => sum + (at.atomicTests?.length || 1), 0))}
                    </span>
                  )}
                </div>
                <FontAwesomeIcon
                  icon={showAtomicTestsPanel ? faChevronUp : faChevronDown}
                  className="text-orange-500"
                />
              </button>

              {showAtomicTestsPanel && (
                <div className="mt-2 border border-orange-200 rounded-lg p-4 bg-white max-h-96 overflow-y-auto">
                  {loadingAtomicTests ? (
                    <div className="text-center py-4">
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-orange-500 text-2xl" />
                      <p className="mt-2 text-gray-600">{t('actions.loadingAtomicTests')}</p>
                    </div>
                  ) : availableAtomicTests.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      <p>{t('actions.noAtomicTests')}</p>
                      <p className="text-sm mt-1">{t('actions.canStillLogCustomAction')}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {availableAtomicTests.map((test) => (
                        <div key={test._id} className={`border rounded-lg p-3 ${darkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{test.displayName || test.name}</h4>
                              <p className={`text-sm mt-1 line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{test.description}</p>

                              {/* Platform badges */}
                              <div className="flex flex-wrap gap-1 mt-2">
                                {test.platforms?.map((platform, idx) => (
                                  <span
                                    key={idx}
                                    className={`px-2 py-0.5 text-xs rounded-full text-white ${atomicService.getPlatformColor(platform)}`}
                                  >
                                    {platform}
                                  </span>
                                ))}
                                <span className="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">
                                  {test.executorType || 'manual'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Individual tests within this technique */}
                          {test.atomicTests && test.atomicTests.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {test.atomicTests.map((individualTest, idx) => (
                                <div
                                  key={idx}
                                  className="pl-3 border-l-2 border-orange-300 py-2"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-gray-800">
                                        {individualTest.name}
                                      </p>
                                      {individualTest.description && (
                                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                                          {individualTest.description}
                                        </p>
                                      )}
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {individualTest.supportedPlatforms?.map((p, i) => (
                                          <span key={i} className="text-xs text-gray-500">{p}</span>
                                        ))}
                                        {individualTest.executor?.elevationRequired && (
                                          <span className="text-xs text-red-600">⬆ Admin</span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex gap-1 ml-2">
                                      <button
                                        type="button"
                                        onClick={() => handleExecuteTest(test, individualTest)}
                                        className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 flex items-center"
                                        title={t('actions.executeOnAgent')}
                                      >
                                        <FontAwesomeIcon icon={faPlay} className="mr-1" />
                                        {t('common.execute')}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleUseAsTemplate(test, individualTest)}
                                        className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200 flex items-center"
                                        title={t('actions.useAsTemplate')}
                                      >
                                        <FontAwesomeIcon icon={faFileAlt} className="mr-1" />
                                        {t('common.template')}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Single test (no nested atomicTests) */}
                          {(!test.atomicTests || test.atomicTests.length === 0) && (
                            <div className="mt-2 flex gap-2">
                              <button
                                type="button"
                                onClick={() => handleExecuteTest(test, {
                                  name: test.name,
                                  description: test.description,
                                  executor: { type: test.executorType, command: test.executorCommand },
                                  inputArguments: test.inputArguments,
                                  supportedPlatforms: test.platforms
                                })}
                                className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 flex items-center"
                              >
                                <FontAwesomeIcon icon={faPlay} className="mr-1" />
                                {t('common.execute')}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleUseAsTemplate(test, {
                                  name: test.name,
                                  description: test.description,
                                  executor: { type: test.executorType, command: test.executorCommand },
                                  inputArguments: test.inputArguments,
                                  supportedPlatforms: test.platforms
                                })}
                                className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded hover:bg-orange-200 flex items-center"
                              >
                                <FontAwesomeIcon icon={faFileAlt} className="mr-1" />
                                {t('common.template')}
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Operator */}
          <div className="mb-4">
            <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} htmlFor="operator">
              {t('actions.operator')} *
            </label>
            {teamMembers.length > 0 ? (
              <select
                id="operator"
                name="operator"
                value={formData.operator}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                required
                disabled={loading}
              >
                <option value="">{t('actions.selectOperator')}</option>
                {teamMembers.map((member, index) => (
                  <option key={index} value={member.name}>
                    {member.name} - {member.role}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                id="operator"
                name="operator"
                value={formData.operator}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                required
                disabled={loading}
                placeholder={t('actions.enterOperatorName')}
              />
            )}
            {teamMembers.length === 0 && (
              <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('actions.noTeamMembersHint')}
              </p>
            )}
          </div>

          {/* Source and Target Selection - Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Source Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={`block text-sm font-bold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('actions.sourceAttackOrigin')}
                </label>
                <button
                  type="button"
                  onClick={() => setShowAddTargetModal(true)}
                  className={`text-xs flex items-center ${darkMode ? 'text-orange-400 hover:text-orange-300' : 'text-orange-600 hover:text-orange-800'}`}
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1" />
                  {t('actions.addTarget')}
                </button>
              </div>
              {availableTargets.length === 0 ? (
                <div className={`rounded-lg p-4 text-center h-full flex flex-col justify-center ${darkMode ? 'bg-orange-900/30' : 'bg-orange-50'}`}>
                  <p className={`mb-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('actions.noTargetsDefined')}</p>
                  <button
                    type="button"
                    onClick={() => setShowAddTargetModal(true)}
                    className={`text-sm font-medium ${darkMode ? 'text-orange-400 hover:text-orange-300' : 'text-orange-600 hover:text-orange-800'}`}
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-1" />
                    {t('actions.addFirstTarget')}
                  </button>
                </div>
              ) : (
                <div className={`rounded-lg p-4 ${darkMode ? 'bg-orange-900/30' : 'bg-orange-50'}`}>
                  <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {t('actions.sourceDescription')}
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {availableTargets.map((target) => (
                      <label
                        key={target._id}
                        className="flex items-center p-2 hover:bg-orange-100 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedSources.includes(target._id)}
                          onChange={() => handleSourceToggle(target._id)}
                          className="mr-3 h-4 w-4 text-orange-600 rounded focus:ring-orange-500"
                          disabled={loading}
                        />
                        <div className="flex-1">
                          <div className="flex items-center">
                            <FontAwesomeIcon
                              icon={target.type === 'ip' ? faGlobe : faDesktop}
                              className="mr-2 text-orange-600"
                            />
                            <span className="font-medium text-sm">{target.value}</span>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                  {selectedSources.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-orange-200">
                      <p className="text-xs text-gray-600">
                        {(t('actions.sourcesSelected') || '{{count}} source(s) selected').replace('{{count}}', selectedSources.length)}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Target Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={`block text-sm font-bold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('actions.affectedTargets')}
                </label>
                <button
                  type="button"
                  onClick={() => setShowAddTargetModal(true)}
                  className={`text-xs flex items-center ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1" />
                  {t('actions.addTarget')}
                </button>
              </div>
              {availableTargets.length === 0 ? (
                <div className={`rounded-lg p-4 text-center h-full flex flex-col justify-center ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                  <p className={`mb-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('actions.noTargetsDefined')}</p>
                  <button
                    type="button"
                    onClick={() => setShowAddTargetModal(true)}
                    className={`text-sm font-medium ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-1" />
                    {t('actions.addFirstTarget')}
                  </button>
                </div>
              ) : (
                <div className={`rounded-lg p-4 ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                  <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {t('actions.whichTargetsAffected')}
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {availableTargets.map((target) => (
                      <label
                        key={target._id}
                        className={`flex items-center p-2 rounded cursor-pointer ${darkMode ? 'hover:bg-blue-900/50' : 'hover:bg-blue-100'}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedTargets.includes(target._id)}
                          onChange={() => handleTargetToggle(target._id)}
                          className="mr-3 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          disabled={loading}
                        />
                        <div className="flex-1">
                          <div className="flex items-center">
                            <FontAwesomeIcon
                              icon={target.type === 'ip' ? faGlobe : faDesktop}
                              className={`mr-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}
                            />
                            <span className={`font-medium text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{target.value}</span>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                  {selectedTargets.length > 0 && (
                    <div className={`mt-2 pt-2 ${darkMode ? 'border-t border-blue-800' : 'border-t border-blue-200'}`}>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {(t('actions.targetsSelected') || '{{count}} target(s) selected').replace('{{count}}', selectedTargets.length)}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="mb-4">
            <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} htmlFor="notes">
              {t('actions.additionalNotes')}
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'}`}
              disabled={loading}
              placeholder={t('actions.notesPlaceholder')}
            />
          </div>

          {/* Evidence URLs */}
          <div className="mb-6">
            <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('actions.evidence')}
            </label>
            <div className="space-y-2">
              {formData.evidence.map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  {item.startsWith('data:image') ? (
                    // Image preview
                    <div className={`flex items-center flex-1 p-2 rounded-md ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                      <img
                        src={item}
                        alt="Evidence"
                        className="h-20 w-20 object-cover rounded mr-3"
                      />
                      <span className={`text-sm flex-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Screenshot {index + 1}
                      </span>
                    </div>
                  ) : (
                    // URL input
                    <input
                      type="url"
                      value={item}
                      readOnly
                      className={`flex-1 px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => handleEvidenceRemove(index)}
                    className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    {t('common.remove')}
                  </button>
                </div>
              ))}

              {/* Add evidence buttons */}
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={handleEvidenceAdd}
                  className={`px-3 py-2 rounded-md ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1" />
                  {t('actions.addUrl')}
                </button>

                <label className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer">
                  <FontAwesomeIcon icon={faCamera} className="mr-1" />
                  {t('actions.addScreenshot')}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleEvidenceFileUpload}
                    className="hidden"
                  />
                </label>
              </div>

              <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('actions.evidenceHint')}
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(`/projects/${projectId}`)}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-400"
              disabled={loading}
            >
              {loading ? t('common.saving') : (isEditing ? t('actions.updateAction') : t('actions.logAction'))}
            </button>
          </div>
        </form>
      </div>

      {/* MITRE Technique Selector Modal */}
      {showTechniqueSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-xl font-bold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faShieldAlt} className="mr-2 text-red-600" />
                {t('actions.selectMitreTechnique')}
                <span className={`ml-2 text-sm font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  (with Atomic Tests)
                </span>
              </h2>
              <button
                type="button"
                onClick={handleTechniqueSelectorClose}
                className={`p-2 rounded-full transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
            <MitreTechniqueSelector
              onSelect={handleTechniqueSelect}
              selectedTechnique={formData.mitreAttackTechnique?.id}
              onClose={handleTechniqueSelectorClose}
              darkMode={darkMode}
            />
          </div>
        </div>
      )}

      {/* Execute Atomic Test Modal */}
      {showExecuteModal && executeTestData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-xl font-bold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faPlay} className="mr-2 text-green-600" />
                {t('actions.executeAtomicTest')}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowExecuteModal(false);
                  setExecuteTestData(null);
                  setExecuteResult(null);
                }}
                className={`p-2 rounded-full transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Test Info */}
              <div className={`mb-4 p-3 rounded-lg ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{executeTestData.individualTest.name}</h3>
                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{executeTestData.individualTest.description}</p>
                <div className="flex gap-2 mt-2">
                  <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                    {executeTestData.test.techniqueId}
                  </span>
                  <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded">
                    {executeTestData.individualTest.executor?.type || 'manual'}
                  </span>
                </div>
              </div>

              {/* Agent Selection */}
              <div className="mb-4">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FontAwesomeIcon icon={faRobot} className="mr-1" />
                  {t('actions.selectTargetAgent')}
                </label>
                {loadingAgents ? (
                  <div className="text-center py-4">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-500" />
                    <span className={`ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('actions.loadingAgents')}</span>
                  </div>
                ) : availableAgents.length === 0 ? (
                  <div className={`p-4 border rounded-lg text-center ${darkMode ? 'bg-yellow-900/30 border-yellow-700' : 'bg-yellow-50 border-yellow-200'}`}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500 text-2xl mb-2" />
                    <p className={darkMode ? 'text-yellow-400' : 'text-yellow-700'}>{t('actions.noOnlineAgents')}</p>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-yellow-500' : 'text-yellow-600'}`}>
                      {t('actions.ensureAgentRunning')}
                    </p>
                  </div>
                ) : (
                  <select
                    value={selectedAgentId}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  >
                    {availableAgents.map(agent => (
                      <option key={agent._id} value={agent._id}>
                        {agent.hostname} ({agent.platform}) - {agent.ipAddress}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Input Arguments */}
              {executeTestData.individualTest.inputArguments &&
               executeTestData.individualTest.inputArguments.length > 0 && (
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('actions.testParameters')}
                  </label>
                  <div className="space-y-3">
                    {executeTestData.individualTest.inputArguments.map(arg => (
                      <div key={arg.name}>
                        <label className={`block text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {arg.name}
                          {arg.description && (
                            <span className={darkMode ? 'text-gray-500 ml-1' : 'text-gray-400 ml-1'}>- {arg.description}</span>
                          )}
                        </label>
                        <input
                          type="text"
                          value={executeInputValues[arg.name] || ''}
                          onChange={(e) => setExecuteInputValues(prev => ({
                            ...prev,
                            [arg.name]: e.target.value
                          }))}
                          placeholder={`Default: ${arg.default || 'none'}`}
                          className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Command Preview */}
              {executeTestData.individualTest.executor?.command && (
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('actions.commandPreview')}
                  </label>
                  <pre className={`p-3 rounded-md text-xs overflow-x-auto ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-800 text-gray-100'}`}>
                    {atomicService.substituteVariables(
                      executeTestData.individualTest.executor.command,
                      executeTestData.individualTest.inputArguments,
                      executeInputValues
                    )}
                  </pre>
                </div>
              )}

              {/* Execution Result */}
              {executeResult && (
                <div className={`mb-4 p-4 rounded-lg ${
                  executeResult.success
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex items-center">
                    <FontAwesomeIcon
                      icon={executeResult.success ? faCheckCircle : faExclamationTriangle}
                      className={executeResult.success ? 'text-green-500' : 'text-red-500'}
                    />
                    <span className={`ml-2 font-medium ${
                      executeResult.success ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {executeResult.message}
                    </span>
                  </div>
                  {executeResult.success && executeResult.actionId && (
                    <p className="text-sm text-green-600 mt-2">
                      {t('actions.actionLoggedAutomatically')}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className={`p-4 border-t flex justify-end gap-3 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                type="button"
                onClick={() => {
                  setShowExecuteModal(false);
                  setExecuteTestData(null);
                  setExecuteResult(null);
                }}
                className={`px-4 py-2 rounded-md ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleExecuteConfirm}
                disabled={executing || !selectedAgentId || availableAgents.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed flex items-center"
              >
                {executing ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    {t('actions.executing')}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlay} className="mr-2" />
                    {t('actions.executeTest')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Target Modal */}
      {showAddTargetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg shadow-xl w-full max-w-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-bold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faPlus} className="mr-2 text-blue-600" />
                {t('targets.addNewTarget')}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowAddTargetModal(false);
                  setNewTarget({ type: 'hostname', value: '', description: '' });
                }}
                className={`p-2 rounded-full transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Target Type */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('targets.targetType')}
                </label>
                <select
                  value={newTarget.type}
                  onChange={(e) => setNewTarget(prev => ({ ...prev, type: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="hostname">{t('targets.hostname')}</option>
                  <option value="ip">{t('targets.ipAddress')}</option>
                  <option value="url">{t('targets.url')}</option>
                  <option value="range">{t('targets.ipRange')}</option>
                  <option value="subnet">{t('targets.subnet')}</option>
                </select>
              </div>

              {/* Target Value */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('common.value')} *
                </label>
                <input
                  type="text"
                  value={newTarget.value}
                  onChange={(e) => setNewTarget(prev => ({ ...prev, value: e.target.value }))}
                  placeholder={
                    newTarget.type === 'ip' ? '192.168.1.100' :
                    newTarget.type === 'hostname' ? 'server.local' :
                    newTarget.type === 'url' ? 'https://example.com' :
                    newTarget.type === 'range' ? '192.168.1.1-192.168.1.254' :
                    '192.168.1.0/24'
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>

              {/* Description */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('common.descriptionOptional')}
                </label>
                <input
                  type="text"
                  value={newTarget.description}
                  onChange={(e) => setNewTarget(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g., Web server, Domain controller"
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
            </div>

            <div className={`p-4 border-t flex justify-end gap-3 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                type="button"
                onClick={() => {
                  setShowAddTargetModal(false);
                  setNewTarget({ type: 'hostname', value: '', description: '' });
                }}
                className={`px-4 py-2 rounded-md ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleAddTarget}
                disabled={addingTarget || !newTarget.value.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center"
              >
                {addingTarget ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    {t('common.adding')}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    {t('actions.addTarget')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionForm;